import { createEffect, createSignal } from "solid-js";
import { trace } from "../lib/trace";

export type Theme = "light" | "dark";

const getInitialTheme = (): Theme => {
  if (typeof window === "undefined") return "light";

  const stored = localStorage.getItem("theme") as Theme | null;
  if (stored && (stored === "light" || stored === "dark")) {
    return stored;
  }

  // Check system preference
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return "light";
};

// Initialize theme immediately
const initialTheme = getInitialTheme();
if (typeof window !== "undefined") {
  document.documentElement.setAttribute("data-theme", initialTheme);
}

const [theme, setTheme] = createSignal<Theme>(initialTheme);

export const useTheme = () => {
  const toggleTheme = () => {
    setTheme((prev) => {
      const newTheme = prev === "light" ? "dark" : "light";
      trace.state("theme toggled", { from: prev, to: newTheme });
      localStorage.setItem("theme", newTheme);
      document.documentElement.setAttribute("data-theme", newTheme);
      return newTheme;
    });
  };

  // Initialize theme on mount
  createEffect(() => {
    document.documentElement.setAttribute("data-theme", theme());
  });

  return {
    theme,
    toggleTheme,
    isDark: () => theme() === "dark",
  };
};
